import type React from 'react'
import type { AccessContext, Organization } from '../../../types/access'
import type { IndustryLabelKey } from '../../../hooks/useIndustryTerminology'

export type WorkspaceView =
  | 'platform'
  | 'admin'
  | 'learning'
  | 'personal-jobbank'
  | 'job-bank'
  | 'market-opportunities'
  | 'mentorship-admin'
  | 'worker-marketplace'
  | 'worker-mentorship'
  | 'shift'
  | 'supervisor'
  | 'mapper'
  | 'super'
  | 'rbac'
  | 'billing'
  | 'platform-pricing'
  | 'sales'
  | 'sales-crm'

export type AdminTab =
  | 'overview'
  | 'employment'
  | 'organizations'
  | 'relationships'
  | 'org-tree'
  | 'users-access'
  | 'locations'
  | 'workers'
  | 'skills'
  | 'schedule'
  | 'jobs'
  | 'worklog'
  | 'timesheets'
  | 'decision-reviews'
  | 'payroll'
  | 'messages'
  | 'reports'
  | 'forecasting'
  | 'settings'

export type NavigationDomain = 'platform' | 'tenant' | 'product'

export type NavigationGroupKey =
  | 'home'
  | 'people'
  | 'work'
  | 'operations'
  | 'network'
  | 'approvals'
  | 'settlement'
  | 'communication'
  | 'settings'
  | 'platform'
  | 'learning'
  | 'tools'
  | 'legacy'

export type NavigationContextRequirement = 'none' | 'tenant' | 'organization'

export type NavigationIconKey =
  | 'dashboard'
  | 'organization'
  | 'relationships'
  | 'org-tree'
  | 'users'
  | 'workers'
  | 'skills'
  | 'jobs'
  | 'schedule'
  | 'worklog'
  | 'locations'
  | 'timesheets'
  | 'payroll'
  | 'messages'
  | 'reports'
  | 'forecasting'
  | 'settings'
  | 'platform'
  | 'learning'
  | 'mentor'
  | 'worker'
  | 'supervisor'
  | 'mapper'
  | 'billing'
  | 'sales'
  | 'legacy'

export interface NavigationLabelContext {
  t: (value: string) => string
  explicitIndustry?: boolean
  label?: (key: IndustryLabelKey) => string
}

export interface NavigationAccessInput {
  accessContext: AccessContext | null
  selectedTenantId?: string
  currentOrganization?: Organization | null
  hasAnyPermission: (permissionKeys: string[]) => boolean
}

export interface AdminNavigationEntry {
  key: AdminTab
  route: string
  workspaceKey: AdminTab
  domain: 'tenant'
  group: NavigationGroupKey
  order: number
  icon: NavigationIconKey
  resolveLabel: (context: NavigationLabelContext) => string
  resolveDescription?: (context: NavigationLabelContext) => string
  contextRequirement: NavigationContextRequirement
  requiredPermissions?: string[]
  isVisible: (context: NavigationAccessInput) => boolean
  aliases?: string[]
  contextRequiredExplanation?: string
  industryLabelKey?: IndustryLabelKey
  billingFeatureKey?: string
}

export interface WorkspaceNavigationEntry {
  key: WorkspaceView
  route: string
  workspaceKey: WorkspaceView
  domain: NavigationDomain
  group: NavigationGroupKey
  order: number
  icon: NavigationIconKey
  resolveLabel: (context: NavigationLabelContext) => string
  resolveDescription: (context: NavigationLabelContext) => string
  cta: string
  feature: string
  contextRequirement: NavigationContextRequirement
  isVisible: (context: NavigationAccessInput) => boolean
  isDisabled?: (context: NavigationAccessInput) => boolean
  aliases?: string[]
  contextRequiredExplanation?: string
  billingFeatureKey?: string
}

export const WORKSPACE_VIEWS: WorkspaceView[] = [
  'platform',
  'admin',
  'learning',
  'personal-jobbank',
  'job-bank',
  'market-opportunities',
  'mentorship-admin',
  'worker-marketplace',
  'worker-mentorship',
  'shift',
  'supervisor',
  'mapper',
  'super',
  'rbac',
  'billing',
  'platform-pricing',
  'sales',
  'sales-crm',
]

export const isWorkspaceView = (value: string | null | undefined): value is WorkspaceView =>
  Boolean(value) && (WORKSPACE_VIEWS as string[]).includes(value as string)

const memberships = (accessContext: AccessContext | null) => accessContext?.memberships || accessContext?.tenantMemberships || []

export const isWorkerMember = (accessContext: AccessContext | null) =>
  memberships(accessContext).some((membership) => membership.membership_type === 'worker')

export const isSystemOrPlatformAdmin = (accessContext: AccessContext | null) => Boolean(
  accessContext?.isSystemAdmin
  || accessContext?.isSystemSuperAdmin
  || accessContext?.isPlatformAdmin
  || accessContext?.isGeneralSystemsAdmin
)

export const isPlatformUser = (accessContext: AccessContext | null) => Boolean(
  accessContext?.isSystemAdmin
  || accessContext?.isSystemSuperAdmin
  || accessContext?.isPlatformAdmin
  || accessContext?.isPlatformDelegate
  || accessContext?.canManageTenants
  || accessContext?.canViewPlatformBilling
  || accessContext?.canBypassTenantSetup
)

export const hasExplicitTenantContext = ({ accessContext, selectedTenantId }: NavigationAccessInput) => (
  Boolean(accessContext?.tenantId || selectedTenantId)
)

/**
 * Backend-authoritative workspace availability.
 *
 * `/api/access/my-context` returns `uiCapabilities.workspaces`, a per-workspace
 * answer to "may this authenticated user open this?". When present it is the
 * ONLY input — the registry describes what exists on the platform, never what
 * this user may see. The local predicates below remain as a compatibility
 * fallback for a backend that predates the capability map, and are also what
 * the tests exercise for that older shape.
 */
export const backendWorkspaceAvailability = (
  accessContext: AccessContext | null,
  workspace: WorkspaceView
): boolean | undefined => {
  const workspaces = accessContext?.uiCapabilities?.workspaces as Record<string, boolean> | undefined
  if (!workspaces || typeof workspaces !== 'object') return undefined
  const value = workspaces[workspace]
  return typeof value === 'boolean' ? value : undefined
}

/** Resolve a workspace predicate, preferring the backend's own answer. */
const backendFirst = (
  workspace: WorkspaceView,
  fallback: (context: NavigationAccessInput) => boolean
) => (context: NavigationAccessInput): boolean => {
  const authoritative = backendWorkspaceAvailability(context.accessContext, workspace)
  return authoritative === undefined ? fallback(context) : authoritative
}

const legacy_canOpenPlatformWorkspace = ({ accessContext }: NavigationAccessInput) =>
  isSystemOrPlatformAdmin(accessContext) || Boolean(accessContext?.canManageTenants)

/**
 * A worker legitimately has `visibleOrganizations` (their agency and the client
 * company they are placed with) and may have `accessibleLocations` (their
 * worksite). Those exist so the worker UI can show branding and worksite
 * context — they are NOT administrative authorization. Treating them as such is
 * what exposed the Admin workspace to worker-only accounts.
 */
/**
 * Workspaces that are part of the WORKER experience rather than destinations to
 * switch between. The worker application reaches all of these from inside
 * itself, so surfacing them in a workspace switcher would be duplication.
 */
export const WORKER_EXPERIENCE_WORKSPACES: ReadonlySet<string> = new Set([
  'worker-marketplace',
  'worker-mentorship',
  'personal-jobbank',
  'learning',
])

export const isWorkerOnlyMember = (accessContext: AccessContext | null) =>
  accessContext?.uiCapabilities?.isWorkerOnlyMember === true

export const canOpenAdminWorkspace = (context: NavigationAccessInput): boolean => {
  const authoritative = backendWorkspaceAvailability(context.accessContext, 'admin')
  if (authoritative !== undefined) return authoritative
  return legacyCanOpenAdminWorkspace(context)
}

const legacyCanOpenAdminWorkspace = ({ accessContext, hasAnyPermission }: NavigationAccessInput) => Boolean(
  accessContext?.uiCapabilities?.canAccessAdminWorkspace
  || accessContext?.corporateProfile
  || (!isWorkerOnlyMember(accessContext) && (
    accessContext?.visibleOrganizations?.length
    || accessContext?.organizations?.length
    || accessContext?.accessibleLocations?.length
  ))
) || hasAnyPermission([
  'tenant.dashboard.view',
  'tenant.organizations.manage',
  'tenant.rbac.manage',
  'tenant.branding.manage',
  'tenant.locations.view',
  'tenant.locations.manage',
  'tenant.jobs.view',
  'tenant.workers.view',
])

const legacy_canOpenBillingWorkspace = ({ accessContext, hasAnyPermission }: NavigationAccessInput) => Boolean(
  accessContext?.uiCapabilities?.modules?.billing
  || accessContext?.uiCapabilities?.modules?.platformBilling
  || accessContext?.canViewTenantBilling
  || accessContext?.canManageTenantBilling
  || accessContext?.canViewPlatformBilling
  || accessContext?.canManagePlatformBilling
  || accessContext?.isSystemAdmin
  || accessContext?.isSystemSuperAdmin
) || hasAnyPermission([
  'tenant.billing.view',
  'tenant.billing.manage',
  'billing.access',
  'platform.billing.view',
  'platform.billing.manage',
])

const legacy_canOpenPlatformPricingWorkspace = ({ accessContext, hasAnyPermission }: NavigationAccessInput) => Boolean(
  accessContext?.isSystemAdmin || accessContext?.isSystemSuperAdmin
) || hasAnyPermission(['platform.billing.pricing.view', 'platform.billing.pricing.manage'])

const legacy_canOpenSalesWorkspace = ({ accessContext, hasAnyPermission }: NavigationAccessInput) => Boolean(
  accessContext?.uiCapabilities?.modules?.platformSales
  || accessContext?.canViewPlatformSales
  || accessContext?.canManagePlatformSales
  || accessContext?.isSystemAdmin
  || accessContext?.isSystemSuperAdmin
  || accessContext?.canManageTenants
) || hasAnyPermission([
  'platform.sales.view',
  'platform.sales.manage',
  'platform.commissions.view',
  'platform.commissions.manage',
  'platform.billing.view',
  'platform.billing.manage',
])

const legacy_canOpenWorkerMarketplaceWorkspace = ({ accessContext }: NavigationAccessInput) =>
  isWorkerMember(accessContext) || accessContext?.requiresOrgLink === true

const legacy_canOpenWorkerMentorshipWorkspace = (context: NavigationAccessInput) =>
  Boolean(context.accessContext?.uiCapabilities?.modules?.workerMentorship) || canOpenWorkerMarketplaceWorkspace(context)

const legacy_canOpenMentorshipAdminWorkspace = ({ accessContext }: NavigationAccessInput) => {
  const explicitMentorPermissions = new Set(accessContext?.mentorPermissions || [])
  return Boolean(
    accessContext?.canAccessMentorAdmin
    || accessContext?.uiCapabilities?.modules?.mentorAdmin
    || accessContext?.uiCapabilities?.modules?.mentorshipAdmin
    || accessContext?.uiCapabilities?.modules?.mentorship
    || accessContext?.isSystemAdmin
    || accessContext?.isSystemSuperAdmin
    || [
      'mentorship.admin.access',
      'mentorship.manage',
      'mentorship.ai_mentors.manage',
      'mentorship.human_mentors.manage',
      'mentorship.learners.view',
      'mentorship.skills.manage',
      'mentorship.mentor_approvals.review',
      'mentorship.matches.manage',
      'mentorship.assignments.manage',
      'mentorship.sessions.view',
      'mentorship.sessions.manage',
      'mentorship.reviews.view',
      'mentorship.pricing.manage',
      'mentorship.payments.view',
      'mentorship.payments.manage',
      'mentorship.disputes.manage',
      'mentorship.llm_settings.manage',
      'mentorship.reports.view',
    ].some((permission) => explicitMentorPermissions.has(permission))
  )
}

const legacy_canOpenSupervisorWorkspace = ({ accessContext, hasAnyPermission }: NavigationAccessInput) =>
  Boolean(accessContext?.accessibleLocations?.length) || hasAnyPermission(['view.supervisor', 'tenant.locations.view'])

const legacy_canOpenLearningWorkspace = ({ accessContext }: NavigationAccessInput) =>
  Boolean(accessContext) && accessContext?.canAccessLearning !== false

/**
 * Job Bank is an employer/agency tool: it surfaces workforce matches, employer
 * ownership claims, and agency pursuit pipelines. It is never a worker's own
 * job-search surface — workers discover work through Shifts → Find Work, which
 * uses the worker marketplace APIs.
 *
 * The old fallback treated ANY feature state that was not `not_available` or
 * `platform_only` as access. Because `marketplace.external_job_bank` is seeded
 * enabled for every plan, that made Job Bank visible to every user in an
 * entitled tenant, workers included. Tenant entitlement is a commercial gate,
 * not an authorization one.
 */
export const canOpenJobBankWorkspace = (context: NavigationAccessInput) => {
  const authoritative = backendWorkspaceAvailability(context.accessContext, 'job-bank')
  if (authoritative !== undefined) return authoritative
  const capabilities = context.accessContext?.uiCapabilities?.marketDemand
    || context.accessContext?.featureFlagsOrUiCapabilities?.marketDemand
  return capabilities?.canUseExternalJobBank === true && !isWorkerOnlyMember(context.accessContext)
}

export const canOpenAgencyMarketOpportunitiesWorkspace = (context: NavigationAccessInput) => {
  const authoritative = backendWorkspaceAvailability(context.accessContext, 'market-opportunities')
  if (authoritative !== undefined) return authoritative
  const capabilities = context.accessContext?.uiCapabilities?.marketDemand
    || context.accessContext?.featureFlagsOrUiCapabilities?.marketDemand
  return capabilities?.canUseAgencyMarketOpportunities === true
    && Boolean(capabilities.agencyOrganizationIds?.length)
    && !isWorkerOnlyMember(context.accessContext)
}

const hasPermissions = (entry: Pick<AdminNavigationEntry, 'requiredPermissions'>, context: NavigationAccessInput) =>
  !entry.requiredPermissions || context.hasAnyPermission(entry.requiredPermissions)

const isTenantEntryVisible = (entry: Pick<AdminNavigationEntry, 'requiredPermissions'>, context: NavigationAccessInput) =>
  hasExplicitTenantContext(context) && hasPermissions(entry, context)

const visibleWithPermissions = (requiredPermissions?: string[]) => (context: NavigationAccessInput) =>
  isTenantEntryVisible({ requiredPermissions }, context)

export const ADMIN_NAVIGATION_ENTRIES: AdminNavigationEntry[] = [
  {
    key: 'employment', route: '/admin?workspace=employment', workspaceKey: 'employment',
    domain: 'tenant', group: 'people', order: 15, icon: 'workers',
    resolveLabel: () => 'Employment & Compliance', contextRequirement: 'organization',
    requiredPermissions: ['tenant.employment.manage', 'tenant.employment.verify'],
    billingFeatureKey: 'workforce.employment_compliance',
    isVisible: context => context.accessContext?.billingEntitlements?.features?.['workforce.employment_compliance']?.enabled === true
      && visibleWithPermissions(['tenant.employment.manage', 'tenant.employment.verify'])(context),
    contextRequiredExplanation: 'Select an organization to administer onboarding and verification.',
  },
  {
    key: 'overview',
    route: '/admin',
    workspaceKey: 'overview',
    domain: 'tenant',
    group: 'home',
    order: 10,
    icon: 'dashboard',
    resolveLabel: () => 'Network Dashboard',
    contextRequirement: 'tenant',
    isVisible: (context) => hasExplicitTenantContext(context),
    aliases: ['dashboard'],
    contextRequiredExplanation: 'Select a tenant to view tenant dashboard metrics.',
  },
  {
    key: 'workers',
    route: '/admin?workspace=workers',
    workspaceKey: 'workers',
    domain: 'tenant',
    group: 'people',
    order: 10,
    icon: 'workers',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? label('workers') : 'Staff / Workers',
    contextRequirement: 'organization',
    requiredPermissions: ['tenant.workers.view'],
    isVisible: visibleWithPermissions(['tenant.workers.view']),
    aliases: ['workforce', 'staff'],
    industryLabelKey: 'workers',
    contextRequiredExplanation: 'Select an organization to review staff and worker records.',
  },
  {
    key: 'users-access',
    route: '/admin?workspace=users-access',
    workspaceKey: 'users-access',
    domain: 'tenant',
    group: 'people',
    order: 20,
    icon: 'users',
    resolveLabel: () => 'Users & Access',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.users.view', 'tenant.roles.view'],
    isVisible: visibleWithPermissions(['tenant.users.view', 'tenant.roles.view']),
  },
  {
    key: 'jobs',
    route: '/admin?workspace=jobs',
    workspaceKey: 'jobs',
    domain: 'tenant',
    group: 'work',
    order: 10,
    icon: 'jobs',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? label('jobs') : 'Jobs / Orders',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.jobs.view'],
    isVisible: visibleWithPermissions(['tenant.jobs.view']),
    aliases: ['demand', 'demand-jobs'],
    industryLabelKey: 'jobs',
  },
  {
    key: 'schedule',
    route: '/admin?workspace=schedule',
    workspaceKey: 'schedule',
    domain: 'tenant',
    group: 'work',
    order: 20,
    icon: 'schedule',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? `${label('shifts')} / Schedule` : 'Schedule',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.jobs.view', 'tenant.job_assignments.view', 'tenant.jobs.manage', 'tenant.job_assignments.manage'],
    isVisible: visibleWithPermissions(['tenant.jobs.view', 'tenant.job_assignments.view', 'tenant.jobs.manage', 'tenant.job_assignments.manage']),
    industryLabelKey: 'shifts',
  },
  {
    key: 'worklog',
    billingFeatureKey: 'worklog.project_time',
    route: '/admin?workspace=worklog',
    workspaceKey: 'worklog',
    domain: 'tenant',
    group: 'work',
    order: 30,
    icon: 'worklog',
    resolveLabel: () => 'Project Work Log',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.work_projects.view', 'tenant.work_tasks.view', 'tenant.project_timesheets.view'],
    isVisible: visibleWithPermissions(['tenant.work_projects.view', 'tenant.work_tasks.view', 'tenant.project_timesheets.view']),
  },
  {
    key: 'forecasting',
    billingFeatureKey: 'workforce.predictive_forecasting',
    route: '/admin?workspace=forecasting',
    workspaceKey: 'forecasting',
    domain: 'tenant',
    group: 'work',
    order: 40,
    icon: 'forecasting',
    resolveLabel: () => 'Predictive Forecasting',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.forecasting.view'],
    isVisible: visibleWithPermissions(['tenant.forecasting.view']),
  },
  {
    key: 'locations',
    route: '/admin?workspace=locations',
    workspaceKey: 'locations',
    domain: 'tenant',
    group: 'operations',
    order: 10,
    icon: 'locations',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? `${label('locations')} & Geofences` : 'Locations / Geofences',
    resolveDescription: () => 'Live Map is available inside Locations / Geofences.',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.locations.view', 'tenant.locations.manage'],
    isVisible: visibleWithPermissions(['tenant.locations.view', 'tenant.locations.manage']),
    aliases: ['live-map'],
    industryLabelKey: 'locations',
  },
  {
    key: 'relationships',
    route: '/admin?workspace=relationships',
    workspaceKey: 'relationships',
    domain: 'tenant',
    group: 'network',
    order: 10,
    icon: 'relationships',
    resolveLabel: () => 'Network Relationships',
    contextRequirement: 'organization',
    requiredPermissions: ['tenant.organizations.manage', 'tenant.sharing.manage'],
    isVisible: visibleWithPermissions(['tenant.organizations.manage', 'tenant.sharing.manage']),
    contextRequiredExplanation: 'Select an organization before managing network relationships.',
  },
  {
    key: 'timesheets',
    route: '/admin?workspace=timesheets',
    workspaceKey: 'timesheets',
    domain: 'tenant',
    group: 'approvals',
    order: 10,
    icon: 'timesheets',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? `${label('timesheet')} / Approvals` : 'Timesheet Approvals',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.timesheets.view'],
    isVisible: visibleWithPermissions(['tenant.timesheets.view']),
    industryLabelKey: 'timesheet',
  },
  {
    key: 'decision-reviews',
    billingFeatureKey: 'decisions.appeals',
    route: '/admin?workspace=decision-reviews',
    workspaceKey: 'decision-reviews',
    domain: 'tenant',
    group: 'approvals',
    order: 20,
    icon: 'timesheets',
    resolveLabel: () => 'Decision Reviews',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.decision_reviews.view'],
    isVisible: visibleWithPermissions(['tenant.decision_reviews.view']),
  },
  {
    key: 'payroll',
    billingFeatureKey: 'workforce.payroll_exports',
    route: '/admin?workspace=payroll',
    workspaceKey: 'payroll',
    domain: 'tenant',
    group: 'settlement',
    order: 10,
    icon: 'payroll',
    resolveLabel: ({ explicitIndustry, label }) => explicitIndustry && label ? `${label('payroll')} Export` : 'Payroll Export',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.payroll.export'],
    isVisible: visibleWithPermissions(['tenant.payroll.export']),
    industryLabelKey: 'payroll',
  },
  {
    key: 'messages',
    billingFeatureKey: 'workforce.messaging',
    route: '/admin?workspace=messages',
    workspaceKey: 'messages',
    domain: 'tenant',
    group: 'communication',
    order: 10,
    icon: 'messages',
    resolveLabel: () => 'Messages',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.messages.view', 'tenant.messages.send'],
    isVisible: visibleWithPermissions(['tenant.messages.view', 'tenant.messages.send']),
    aliases: ['threadId'],
  },
  {
    key: 'reports',
    billingFeatureKey: 'workforce.reporting',
    route: '/admin?workspace=reports',
    workspaceKey: 'reports',
    domain: 'tenant',
    group: 'communication',
    order: 20,
    icon: 'reports',
    resolveLabel: () => 'Reports',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.exports.csv', 'tenant.timesheets.view'],
    isVisible: visibleWithPermissions(['tenant.exports.csv', 'tenant.timesheets.view']),
  },
  {
    key: 'organizations',
    route: '/admin?workspace=organizations',
    workspaceKey: 'organizations',
    domain: 'tenant',
    group: 'settings',
    order: 10,
    icon: 'organization',
    resolveLabel: () => 'Organizations',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.organizations.view', 'tenant.organizations.manage'],
    isVisible: visibleWithPermissions(['tenant.organizations.view', 'tenant.organizations.manage']),
  },
  {
    key: 'org-tree',
    route: '/admin?workspace=org-tree',
    workspaceKey: 'org-tree',
    domain: 'tenant',
    group: 'settings',
    order: 20,
    icon: 'org-tree',
    resolveLabel: () => 'Org Structure',
    contextRequirement: 'organization',
    requiredPermissions: ['tenant.organization_units.view', 'tenant.organization_units.manage'],
    isVisible: visibleWithPermissions(['tenant.organization_units.view', 'tenant.organization_units.manage']),
  },
  {
    key: 'skills',
    route: '/admin?workspace=skills',
    workspaceKey: 'skills',
    domain: 'tenant',
    group: 'settings',
    order: 30,
    icon: 'skills',
    resolveLabel: () => 'Skills',
    contextRequirement: 'tenant',
    requiredPermissions: ['tenant.skills.manage', 'tenant.settings.manage'],
    isVisible: visibleWithPermissions(['tenant.skills.manage', 'tenant.settings.manage']),
  },
  {
    key: 'settings',
    route: '/admin?workspace=settings',
    workspaceKey: 'settings',
    domain: 'tenant',
    group: 'settings',
    order: 40,
    icon: 'settings',
    resolveLabel: () => 'Settings',
    resolveDescription: () => 'Branding remains available inside Settings.',
    contextRequirement: 'organization',
    requiredPermissions: ['tenant.settings.manage', 'tenant.branding.manage'],
    isVisible: visibleWithPermissions(['tenant.settings.manage', 'tenant.branding.manage']),
    aliases: ['branding'],
    contextRequiredExplanation: 'Select an organization before changing organization-scoped settings.',
  },
]

export const ADMIN_NAVIGATION_BY_KEY = Object.fromEntries(
  ADMIN_NAVIGATION_ENTRIES.map((entry) => [entry.key, entry]),
) as Record<AdminTab, AdminNavigationEntry>

export const ADMIN_NAVIGATION_GROUP_ORDER: Array<{ key: NavigationGroupKey; label: string; order: number }> = [
  { key: 'home', label: 'Home', order: 10 },
  { key: 'people', label: 'People', order: 20 },
  { key: 'work', label: 'Work', order: 30 },
  { key: 'operations', label: 'Operations', order: 40 },
  { key: 'network', label: 'Network', order: 50 },
  { key: 'approvals', label: 'Approvals', order: 60 },
  { key: 'settlement', label: 'Payroll Export', order: 70 },
  { key: 'communication', label: 'Communication and Insights', order: 80 },
  { key: 'settings', label: 'Settings', order: 90 },
]

export const visibleAdminNavigationEntries = (context: NavigationAccessInput) =>
  ADMIN_NAVIGATION_ENTRIES
    .filter((entry) => entry.isVisible(context))
    .sort((left, right) => {
      const leftGroup = ADMIN_NAVIGATION_GROUP_ORDER.find((group) => group.key === left.group)?.order || 999
      const rightGroup = ADMIN_NAVIGATION_GROUP_ORDER.find((group) => group.key === right.group)?.order || 999
      return leftGroup - rightGroup || left.order - right.order || left.key.localeCompare(right.key)
    })

export const groupedAdminNavigationEntries = (entries: AdminNavigationEntry[]) =>
  ADMIN_NAVIGATION_GROUP_ORDER
    .map((group) => ({
      ...group,
      entries: entries
        .filter((entry) => entry.group === group.key)
        .sort((left, right) => left.order - right.order || left.key.localeCompare(right.key)),
    }))
    .filter((group) => group.entries.length > 0)

export const adminTabIds = new Set<AdminTab>(ADMIN_NAVIGATION_ENTRIES.map((entry) => entry.key))

export const adminTabFromLocation = (): AdminTab | null => {
  if (typeof window === 'undefined') return null
  const tab = new URLSearchParams(window.location.search).get('workspace')
  return resolveAdminTabAlias(tab)
}

export const resolveAdminTabAlias = (value: string | null | undefined): AdminTab | null => {
  if (!value) return null
  if (adminTabIds.has(value as AdminTab)) return value as AdminTab
  const match = ADMIN_NAVIGATION_ENTRIES.find((entry) => entry.aliases?.includes(value))
  return match?.key || null
}

export const adminTabUrl = (tabId: AdminTab, pathname?: string): string => {
  if (typeof window === 'undefined') return '/admin'
  const url = new URL(window.location.href)
  if (pathname) url.pathname = pathname
  const params = url.searchParams
  if (tabId === 'overview') {
    params.delete('workspace')
    params.delete('threadId')
  } else {
    params.set('workspace', tabId)
    if (tabId !== 'messages') params.delete('threadId')
  }
  const search = params.toString()
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
}

export const workspaceFromPath = (pathname: string, search: string, fallback: WorkspaceView = 'admin'): WorkspaceView => {
  if (pathname === '/platform') return 'platform'
  if (pathname === '/jobbank') return 'personal-jobbank'
  if (pathname === '/learning') return 'learning'
  if (pathname === '/billing' || pathname === '/admin/billing') return 'billing'
  if (pathname === '/mentor-admin' || pathname === '/mentorship-admin') return 'mentorship-admin'
  const param = new URLSearchParams(search).get('workspace')
  if (resolveAdminTabAlias(param)) return 'admin'
  return isWorkspaceView(param) ? param : fallback
}

export const workspaceFromLocation = (fallback: WorkspaceView = 'admin'): WorkspaceView => {
  if (typeof window === 'undefined') return fallback
  const { pathname, search } = window.location
  return workspaceFromPath(pathname, search, fallback)
}

export const workspaceUrl = (workspace: WorkspaceView): string =>
  workspace === 'platform' ? '/platform' :
  workspace === 'mentorship-admin' ? '/mentor-admin' :
  workspace === 'personal-jobbank' ? '/jobbank' :
  workspace === 'learning' ? '/learning' :
  workspace === 'billing' ? '/billing' :
  workspace === 'admin' ? '/admin' : `/admin?workspace=${workspace}`

export const WORKSPACE_NAVIGATION_ENTRIES: WorkspaceNavigationEntry[] = [
  {
    key: 'platform',
    route: '/platform',
    workspaceKey: 'platform',
    domain: 'platform',
    group: 'platform',
    order: 10,
    icon: 'platform',
    resolveLabel: () => 'Platform Console',
    resolveDescription: () => 'System-wide administration: tenants, global users, platform billing, sales, and platform settings — no tenant selection required.',
    cta: 'Open Platform Console',
    feature: 'platform.console',
    contextRequirement: 'none',
    isVisible: canOpenPlatformWorkspace,
  },
  {
    key: 'admin',
    route: '/admin',
    workspaceKey: 'admin',
    domain: 'tenant',
    group: 'operations',
    order: 10,
    icon: 'dashboard',
    resolveLabel: () => 'Admin workspace',
    resolveDescription: ({ t }) => t('Tenant operations: organization context, access, workers, jobs, timesheets, messaging, and settings.'),
    cta: 'Open admin',
    feature: 'tenant.organizations.manage',
    contextRequirement: 'tenant',
    isVisible: (context) => canOpenAdminWorkspace(context) || Boolean(context.accessContext?.isSystemSuperAdmin && context.accessContext.availableTenants?.length),
    isDisabled: (context) => Boolean(context.accessContext?.isSystemSuperAdmin && !context.selectedTenantId),
    contextRequiredExplanation: 'Select a tenant before opening tenant Admin operations.',
  },
  {
    key: 'learning',
    route: '/learning',
    workspaceKey: 'learning',
    domain: 'product',
    group: 'learning',
    order: 10,
    icon: 'learning',
    resolveLabel: () => 'Learning',
    resolveDescription: () => 'Your platform learning home: continue learning, recommended skills, daily practice, streaks, and coaching — wherever you work.',
    cta: 'Open Learning',
    feature: 'learning.workspace.access',
    contextRequirement: 'none',
    isVisible: canOpenLearningWorkspace,
  },
  {
    key:'personal-jobbank',route:'/jobbank',workspaceKey:'personal-jobbank',domain:'product',group:'learning',order:20,icon:'jobs',
    resolveLabel:()=> 'My JobBank',resolveDescription:()=> 'Find opportunities, save jobs and track your applications.',cta:'Open My JobBank',
    feature:'jobbank.external_discovery',contextRequirement:'none',
    isVisible:({accessContext})=>backendWorkspaceAvailability(accessContext,'personal-jobbank')===true,
  },
  {
    key: 'job-bank',
    route: '/admin?workspace=job-bank',
    workspaceKey: 'job-bank',
    domain: 'product',
    group: 'tools',
    order: 5,
    icon: 'jobs',
    resolveLabel: () => 'Job Bank',
    resolveDescription: () => 'Browse active external opportunities discovered across configured market sources, separate from Nexus Jobs / Orders.',
    cta: 'Browse external opportunities',
    feature: 'marketplace.external_job_bank',
    contextRequirement: 'tenant',
    isVisible: canOpenJobBankWorkspace,
  },
  {
    key: 'market-opportunities',
    route: '/admin?workspace=market-opportunities',
    workspaceKey: 'market-opportunities',
    domain: 'product',
    group: 'tools',
    order: 6,
    icon: 'jobs',
    resolveLabel: () => 'Market Opportunities',
    resolveDescription: () => 'Manage the selected staffing agency’s private business-development pipeline for external demand.',
    cta: 'Open agency pipeline',
    feature: 'marketplace.agency_opportunities',
    contextRequirement: 'organization',
    isVisible: canOpenAgencyMarketOpportunitiesWorkspace,
  },
  {
    key: 'supervisor',
    route: '/admin?workspace=supervisor',
    workspaceKey: 'supervisor',
    domain: 'product',
    group: 'tools',
    order: 10,
    icon: 'supervisor',
    resolveLabel: () => 'Location supervisor',
    resolveDescription: () => 'Review assigned locations, rosters, breadcrumbs, messages, timesheets, and punch activity.',
    cta: 'Open supervisor',
    feature: 'view.supervisor',
    contextRequirement: 'tenant',
    isVisible: canOpenSupervisorWorkspace,
  },
  {
    key: 'worker-marketplace',
    route: '/admin?workspace=worker-marketplace',
    workspaceKey: 'worker-marketplace',
    domain: 'product',
    group: 'tools',
    order: 20,
    icon: 'worker',
    resolveLabel: () => 'Worker workspace',
    resolveDescription: () => 'Manage today’s work, schedule, availability, timesheets, messages, profile, documents, skills, learning, and mentorship.',
    cta: 'Open worker workspace',
    feature: 'worker.marketplace',
    contextRequirement: 'none',
    isVisible: canOpenWorkerMarketplaceWorkspace,
  },
  {
    key: 'billing',
    route: '/billing',
    workspaceKey: 'billing',
    domain: 'tenant',
    group: 'operations',
    order: 20,
    icon: 'billing',
    resolveLabel: () => 'Billing & plans',
    resolveDescription: () => 'Review this tenant’s subscription, usage, invoices, and plan.',
    cta: 'Manage billing',
    feature: 'billing.access',
    contextRequirement: 'tenant',
    isVisible: canOpenBillingWorkspace,
    aliases: ['/admin/billing', '/admin?workspace=billing'],
  },
  {
    key: 'platform-pricing',
    route: '/admin?workspace=platform-pricing',
    workspaceKey: 'platform-pricing',
    domain: 'platform',
    group: 'platform',
    order: 25,
    icon: 'billing',
    resolveLabel: () => 'Pricing & Products',
    resolveDescription: () => 'Manage the product catalog, markets, customer deals, and price simulations.',
    cta: 'Open pricing workspace',
    feature: 'platform.billing.pricing.manage',
    contextRequirement: 'none',
    isVisible: canOpenPlatformPricingWorkspace,
  },
  {
    key: 'sales',
    route: '/admin?workspace=sales',
    workspaceKey: 'sales',
    domain: 'platform',
    group: 'platform',
    order: 30,
    icon: 'sales',
    resolveLabel: () => 'Sales Administration',
    resolveDescription: () => 'Review platform revenue, client attribution, commissions, and sales representative portfolios.',
    cta: 'Open sales',
    feature: 'platform.sales.view',
    contextRequirement: 'none',
    isVisible: canOpenSalesWorkspace,
  },
  {
    key: 'sales-crm',
    route: '/admin?workspace=sales-crm',
    workspaceKey: 'sales-crm',
    domain: 'platform',
    group: 'platform',
    order: 29,
    icon: 'sales',
    resolveLabel: () => 'Sales CRM',
    resolveDescription: () => 'Manage prospects, contacts, opportunities, follow-ups and personal performance.',
    cta: 'Open Sales CRM',
    feature: 'platform.crm.view',
    contextRequirement: 'none',
    isVisible: context => backendFirst('sales-crm', ({accessContext}) => Boolean(accessContext?.isSystemSuperAdmin))(context),
  },
  {
    key: 'mentorship-admin',
    billingFeatureKey: 'mentorship.admin',
    route: '/mentor-admin',
    workspaceKey: 'mentorship-admin',
    domain: 'product',
    group: 'tools',
    order: 30,
    icon: 'mentor',
    resolveLabel: () => 'Mentor Admin',
    resolveDescription: () => 'Manage mentors, learners, sessions, pricing, and reviews.',
    cta: 'Open Mentor Admin',
    feature: 'mentorship.manage',
    contextRequirement: 'tenant',
    isVisible: canOpenMentorshipAdminWorkspace,
    aliases: ['/mentorship-admin'],
  },
  {
    key: 'worker-mentorship',
    route: '/admin?workspace=worker-mentorship',
    workspaceKey: 'worker-mentorship',
    domain: 'product',
    group: 'tools',
    order: 40,
    icon: 'mentor',
    resolveLabel: () => 'Worker mentorship',
    resolveDescription: () => 'Manage goals, bookings, sessions, and mentor mode.',
    cta: 'Open mentorship',
    feature: 'worker.mentorship',
    contextRequirement: 'none',
    isVisible: canOpenWorkerMentorshipWorkspace,
  },
  {
    key: 'shift',
    route: '/admin?workspace=shift',
    workspaceKey: 'shift',
    domain: 'product',
    group: 'legacy',
    order: 10,
    icon: 'legacy',
    resolveLabel: () => 'Shift imports',
    resolveDescription: () => 'Upload worker shift CSV files and monitor their processing status.',
    cta: 'Manage shifts',
    feature: 'view.shift',
    contextRequirement: 'tenant',
    isVisible: (context) => !canOpenAdminWorkspace(context) && context.hasAnyPermission(['view.shift']),
  },
  {
    key: 'mapper',
    route: '/admin?workspace=mapper',
    workspaceKey: 'mapper',
    domain: 'product',
    group: 'legacy',
    order: 30,
    icon: 'mapper',
    resolveLabel: () => 'Fence mapper',
    resolveDescription: () => 'Launch the geofence mapper without leaving the admin console.',
    cta: 'Open workspace',
    feature: 'view.mapper',
    contextRequirement: 'tenant',
    isVisible: (context) => !canOpenAdminWorkspace(context) && context.hasAnyPermission(['view.mapper']),
  },
  {
    key: 'super',
    route: '/admin?workspace=super',
    workspaceKey: 'super',
    domain: 'platform',
    group: 'legacy',
    order: 90,
    icon: 'legacy',
    resolveLabel: () => 'Super admin workspace',
    resolveDescription: () => 'Legacy super-admin workspace.',
    cta: 'Open workspace',
    feature: 'view.super',
    contextRequirement: 'none',
    isVisible: () => false,
  },
  {
    key: 'rbac',
    route: '/admin?workspace=rbac',
    workspaceKey: 'rbac',
    domain: 'tenant',
    group: 'legacy',
    order: 99,
    icon: 'legacy',
    resolveLabel: () => 'Roles & Access',
    resolveDescription: () => 'Legacy roles workspace.',
    cta: 'Open workspace',
    feature: 'view.rbac',
    contextRequirement: 'tenant',
    isVisible: () => false,
  },
]

export const WORKSPACE_NAVIGATION_BY_KEY = Object.fromEntries(
  WORKSPACE_NAVIGATION_ENTRIES.map((entry) => [entry.key, entry]),
) as Record<WorkspaceView, WorkspaceNavigationEntry>

export const visibleWorkspaceNavigationEntries = (context: NavigationAccessInput) =>
  WORKSPACE_NAVIGATION_ENTRIES
    .filter((entry) => entry.isVisible(context))
    .sort((left, right) => left.order - right.order || left.key.localeCompare(right.key))

/** May this user actually enter this workspace right now? */
const canEnterWorkspace = (workspace: WorkspaceView, context: NavigationAccessInput): boolean => {
  const entry = WORKSPACE_NAVIGATION_BY_KEY[workspace]
  return Boolean(entry && entry.isVisible(context) && !entry.isDisabled?.(context))
}

/**
 * Workspaces that a user may open explicitly but that must never be chosen
 * *automatically* as a landing surface. They are market-demand tools, not a
 * footing: an administrator entitled to the Job Bank still belongs in the Admin
 * workspace on login. Sorting the visible entries by display order and taking
 * the first one is what previously dropped every Job-Bank-entitled admin into
 * Job Bank, because its switcher order (5) sorts ahead of Admin (10).
 */
const NEVER_AUTOMATIC_WORKSPACES: ReadonlySet<WorkspaceView> = new Set([
  'personal-jobbank',
  'job-bank',
  'market-opportunities',
])

/**
 * The single source of truth for "where does this user land when the URL does
 * not say?" — used both for the authenticated default landing and as the
 * redirect target for an unauthorized or unknown workspace.
 *
 * Precedence, highest first:
 *   1. Platform/system identity with no explicit tenant selected → Platform Console.
 *   2. Backend-declared learning-first user (no workforce footing) → Learning.
 *   3. Administrative footing (tenant/org admin, and the supervisor-dashboard
 *      default, which is the Admin workspace scoped to a supervisor) → Admin.
 *   4. Supervisor workspace, when Admin itself is not enterable.
 *   5. Worker footing → Worker workspace.
 *   6. Learning, where the learning-first rule still applies.
 *   7. The first authorized workspace that is safe to enter automatically.
 *
 * Admin and Worker capability are independent: a user holding both a worker
 * profile and Admin permissions resolves to Admin. Viewport, browser, and PWA
 * mode are deliberately not inputs.
 */
export const resolveDefaultWorkspace = (context: NavigationAccessInput): WorkspaceView => {
  const { accessContext, selectedTenantId } = context
  const backendDefault = accessContext?.uiCapabilities?.defaultWorkspace

  const platformFirst = !selectedTenantId
    && canEnterWorkspace('platform', context)
    && (backendDefault === 'platform-console' || Boolean(accessContext?.isSystemSuperAdmin))
  if (platformFirst) return 'platform'

  if (backendDefault === 'billing' && canEnterWorkspace('billing', context)) return 'billing'
  if (backendDefault === 'sales' && canEnterWorkspace('sales', context)) return 'sales'
  if (backendDefault === 'sales-crm' && canEnterWorkspace('sales-crm', context)) return 'sales-crm'
  if (backendDefault === 'platform-pricing' && canEnterWorkspace('platform-pricing', context)) return 'platform-pricing'

  if (backendDefault === 'learning' && canEnterWorkspace('learning', context)) return 'learning'

  if (canEnterWorkspace('admin', context)) return 'admin'
  if (canEnterWorkspace('supervisor', context)) return 'supervisor'
  if (canEnterWorkspace('worker-marketplace', context)) return 'worker-marketplace'
  if (canEnterWorkspace('learning', context)) return 'learning'

  const visible = visibleWorkspaceNavigationEntries(context)
  return visible.find((entry) => !NEVER_AUTOMATIC_WORKSPACES.has(entry.key))?.key
    || visible[0]?.key
    || 'learning'
}

/**
 * Redirect target for an unauthorized or unknown workspace.
 *
 * This previously fell back to `'admin'`, which meant a worker who typed an
 * Admin or Job Bank URL was redirected INTO the Admin workspace — the exact
 * surface they are not allowed to reach. It then fell back to the first visible
 * entry by display order, which sent Job-Bank-entitled admins into Job Bank.
 * Both defaults now run through the one precedence resolver above.
 */
export const fallbackWorkspace = (context: NavigationAccessInput): WorkspaceView =>
  resolveDefaultWorkspace(context)

export const guardedWorkspace = (workspace: WorkspaceView, context: NavigationAccessInput): WorkspaceView => {
  const entry = WORKSPACE_NAVIGATION_BY_KEY[workspace]
  if (!entry || !entry.isVisible(context) || entry.isDisabled?.(context)) return fallbackWorkspace(context)
  return workspace
}

export const workspaceLabel = (workspace: WorkspaceView, labelContext: NavigationLabelContext) =>
  WORKSPACE_NAVIGATION_BY_KEY[workspace]?.resolveLabel(labelContext) || workspace

export const validateNavigationRegistry = () => {
  const duplicateAdminKeys = duplicateValues(ADMIN_NAVIGATION_ENTRIES.map((entry) => entry.key))
  const duplicateWorkspaceKeys = duplicateValues(WORKSPACE_NAVIGATION_ENTRIES.map((entry) => entry.key))
  const duplicateAdminRoutes = duplicateValues(ADMIN_NAVIGATION_ENTRIES.map((entry) => entry.route))
  const tenantProductOverlap = WORKSPACE_NAVIGATION_ENTRIES.filter((entry) => entry.domain === 'product' && entry.key === 'admin')
  return {
    duplicateAdminKeys,
    duplicateWorkspaceKeys,
    duplicateAdminRoutes,
    tenantProductOverlap,
    valid: duplicateAdminKeys.length === 0
      && duplicateWorkspaceKeys.length === 0
      && duplicateAdminRoutes.length === 0
      && tenantProductOverlap.length === 0,
  }
}

const duplicateValues = (values: string[]) => {
  const counts = new Map<string, number>()
  values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1))
  return Array.from(counts.entries()).filter(([, count]) => count > 1).map(([value]) => value)
}

export type NavigationIconComponent = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>

export function canOpenPlatformWorkspace(context: NavigationAccessInput): boolean {
  return Boolean(context.accessContext?.companyIntelligence?.view) || backendFirst('platform', legacy_canOpenPlatformWorkspace)(context)
}

export function canOpenLearningWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('learning', legacy_canOpenLearningWorkspace)(context)
}

export function canOpenWorkerMarketplaceWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('worker-marketplace', legacy_canOpenWorkerMarketplaceWorkspace)(context)
}

export function canOpenWorkerMentorshipWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('worker-mentorship', legacy_canOpenWorkerMentorshipWorkspace)(context)
}

export function canOpenMentorshipAdminWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('mentorship-admin', legacy_canOpenMentorshipAdminWorkspace)(context)
}

export function canOpenSupervisorWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('supervisor', legacy_canOpenSupervisorWorkspace)(context)
}

export function canOpenBillingWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('billing', legacy_canOpenBillingWorkspace)(context)
}

export function canOpenPlatformPricingWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('platform-pricing', legacy_canOpenPlatformPricingWorkspace)(context)
}

export function canOpenSalesWorkspace(context: NavigationAccessInput): boolean {
  return backendFirst('sales', legacy_canOpenSalesWorkspace)(context)
}
